<template>
  <div class="advanced-filters">

    <div class="advanced-filters__top-container">
      <div class="advanced-filters__buttons-holder">
        <base-material-design-icon icon="tune" />
        <regular-filters :options="options"
          :selectedOptions="appliedSelectedOptions"
          :regularFilterConfig="regularFilterConfig"
          @change="updateRegular" />
        <base-dropdown horizontalPosition="auto"
          ref="advancedFiltersDropdown"
          @dropdownOpened="onAdvancedFiltersDropdownOpened"
          @dropdownClosed="onAdvancedFiltersDropdownClosed">
          <template v-slot:trigger>
            <filter-button class="advanced-filters__button"
              label="Advanced"
              :number="appliedSelectedOptions.length"
              :active="appliedSelectedOptions.length > 0"
              ref="target" />
          </template>
          <div class="advanced-filters__dropdown rfx-new-dropdown">
            <div class="advanced-no-data"
              v-if="selectedOptions.length === 0">
              No filter selected
            </div>
            <div class="advanced-filters__rows-holder"
              v-else>
              <p class="advanced-filters__title">Show data for the following conditions: </p>
              <div class="advanced-filters__rows">
                <advanced-filters-row v-for="(item) in selectedOptions"
                  :key="item.id"
                  :options="options"
                  :item="item"
                  :is-validating="isValidating"
                  :is-first="item.isFirst"
                  @delete="deleteItem(item)"
                  @update-item="updateItem($event, item)" />
              </div>
            </div>
            <div class="advanced-controls">
              <div class="advanced-controls__extra-line">
                <button class="advanced-button advanced-button_add-new"
                  :class="{ 'advanced-button_add-new_disabled': selectedOptions.length >= advancedFiltersLimit }"
                  @click.prevent="addNew">
                  <base-material-design-icon class="advanced-button__icon"
                    icon="plusCircle" />
                  Add a condition
                </button>
              </div>
              <div class="advanced-controls__main-line">
                <button class="advanced-button advanced-button_clear"
                  :class="{ 'advanced-button_disabled': resetButtonDisabled }"
                  @click.prevent="clear()">
                  Clear All
                </button>
                <button class="advanced-button advanced-button_apply"
                  @click.prevent="apply()">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </base-dropdown>
      </div>
      <div class="advanced-filters__menu">
        <base-dropdown ref="advancedFiltersMenu">
          <template v-slot:trigger>
            <button class="advanced-filters__menu-button">
              <base-material-design-icon icon="dotsHorizontal" />
            </button>
          </template>
          <ul class="advanced-filters__menu-dropdown rfx-new-dropdown">
            <li class="advanced-filters__menu-item"
              @click="share">
              <base-material-design-icon class="advanced-filters__menu-icon"
                icon="exportVariant" />Share
            </li>
            <li class="advanced-filters__menu-item"
              v-if="exportEnabled"
              @click="download">
              <base-material-design-icon class="advanced-filters__menu-icon"
                icon="download" />Download
            </li>
          </ul>
        </base-dropdown>
      </div>
    </div>
    <div class="advanced-filters__chips-container">
      <advanced-chips-block :copyPage="copyPage"
        v-if="appliedSelectedOptions.length"
        :options="appliedSelectedOptions"
        :replicable="replicable"
        :forceCalculate="forceCalculate"
        @delete-option="deleteChip" />
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import AdvancedFiltersRow from './AdvancedFiltersRow'
import AdvancedChipsBlock from './AdvancedChipsBlock'
import FilterButton from './FilterButton.vue';
import { mapActions } from 'vuex'
import RegularFilters from './RegularFilters.vue';

/**
 * Advanced Filters component. Used in RankZone components and Content Brief Reporting tab
 */
export default {
  name: 'AdvancedFilters',

  components: {
    AdvancedFiltersRow,
    AdvancedChipsBlock,
    FilterButton,
    RegularFilters
  },
  props: {
    /**
     * options received form {@link Config} file and filled in parent component
     */
    options: {
      type: Array,
      default: () => []
    },
    // predefined advanced options received from url query
    predefinedOptions: {
      type: String,
      default: null
    },
    // if false this advanced options component does not support copy/paste functionality
    replicable: {
      type: Boolean,
      default: true
    },
    /**
     * if changed from false to true in {@link AdvancedChipsBlock} should be recalculated chips with
     * it's hack for Reporting tab where loading data flow is different
     */
    forceCalculate: {
      type: Boolean,
      default: false
    },
    // allow copy filter logic without selected filters (copy page link with client id)
    copyPage: {
      type: Boolean,
      default: false
    },
    // display Download item in the right menu
    exportEnabled: Boolean,
    regularFilterConfig: {
      type: Array,
      // default: () => null
    }
  },
  data() {
    return {
      // options selected by user
      selectedOptions: [],
      // options applied by user
      appliedSelectedOptions: [],
      // true if options were applied false if all AO were cleared or after first load page
      isFiltersApplied: false,
      // if true - start validating logic for children. It happens in case when at least one value is invalid
      isValidating: false,
      isAdvancedCollapsed: true,
      // true if there is only one clear row in the list
      // we need it because in empty list should be one empty string
      isPristine: true,
      emptyOption: {
        selectedField: '',
        selectedOperator: '',
        selectedFilter: {}
      },
      predefinedOptionsAmount: 0,
      advancedFiltersLimit: 12,
    }
  },
  watch: {
    options: {
      handler: 'handlePredefinedFilters',
      immediate: true
    },
    clientId(v, prevValue) {
      if (prevValue) {
        if (!this.isAdvancedCollapsed) {
          this.$refs.collapsibleBlock.toggleCollapsedStatus()
        }
        this.clear()
      }
    },
  },
  computed: {
    resetButtonDisabled() {
      return this.appliedSelectedOptions.length === 1 && this.isPristine
    },


    isAllFieldsValid() {
      return this.selectedOptions.every(item => {
        const fieldValid = !_.isEmpty(item.selectedField)
        const filterValue = _.get(item, 'selectedFilter.value')
        const filterValid = Boolean(filterValue) || parseInt(filterValue) === 0
        const operatorValid = !_.isEmpty(item.selectedOperator)
        return fieldValid && filterValid && operatorValid
      })
    },
  },
  methods: {
    ...mapActions('rankZoneCommon', ['resetAutocomplete']),
    /**
     * callback for options props
     * */
    handlePredefinedFilters() {
      // if there are predefined AO received from URL query - we should restore all component data
      if (this.predefinedOptions) {
        const parsedFilters = JSON.parse(this.predefinedOptions)
        this.predefinedOptionsAmount = parsedFilters.length;
        const mappedFilters = parsedFilters.filters.map((item, index) => {
          const option = this.options.find(opt => opt.type === item.selectedField)
          const isFirst = index === 0
          const id = Date.now().valueOf() + index // to avoid coincidence of ids
          return {
            ...option, ...item, isFirst, id
          }
        })
        this.appliedSelectedOptions = mappedFilters
        this.selectedOptions = _.cloneDeep(this.appliedSelectedOptions)
        this.isPristine = false
        this.isFiltersApplied = true
      }
    },
    /**
     * add new row in the table
     * */
    addNew() {
      this.setValidating(false);
      const option = _.cloneDeep(this.emptyOption);
      option.isFirst = (this.selectedOptions.length === 0);
      this.selectedOptions = [...this.selectedOptions, { id: Date.now().valueOf(), ...option }];

    },
    /**
     * clear all data in  the table and emit empty AO data to reload non-filtered parent data
     * */
    clear() {
      this.setValidating(false)
      this.isPristine = true
      this.selectedOptions = []
      this.appliedSelectedOptions = []
      if (this.isFiltersApplied) {
        this.isFiltersApplied = false
      }
    },
    /**
     * delete item in the table
     * */
    deleteItem(item) {
      const itemIndex = this.selectedOptions.findIndex(option => option.id === item.id)
      if (itemIndex === -1) {
        return
      }
      this.selectedOptions.splice(itemIndex, 1)
    },
    /**
     * delete chip i the {@link AdvancedChipsBlock}
     * after deleting chip parent data should be updated
     * */
    deleteChip(option) {
      const index = this.appliedSelectedOptions.findIndex(item => item.id === option.id)
      this.appliedSelectedOptions.splice(index, 1)
      if (this.selectedOptions.length) {
        const deletingOption = this.selectedOptions.find(item => option.id === item.id)
        if (deletingOption) {
          this.selectedOptions.splice(this.selectedOptions.indexOf(deletingOption), 1)
        }
      }
      this.$emit('apply-advanced', this.appliedSelectedOptions)
    },
    /**
     * change item from {@link AdvancedOptionsTableRow}
     * */
    updateItem(value, item) {
      this.isPristine = false
      Object.assign(item, value)
    },
    /**
     * apply items, taken from regular {@link RegularFilters}
     */
    updateRegular(newSelectedOptions) {
      this.selectedOptions = newSelectedOptions;
      this.isPristine = false;
      this.apply();
    },
    /**
     * apply button callback
     * */
    apply() {
      if (this.isPristine) {
        // if there was earlier applied data - we need to clear it and
        // notify parent component about necessity to reload data without AO filtering
        if (this.appliedSelectedOptions.length > 0) {
          this.clear()
          return
        }
        // there we do nothing
        if (this.selectedOptions.length === 1) {
          return
        }
      }
      // if there is AO data and data is valid - notify parent about reload filtered data
      if (this.isAllFieldsValid) {
        this.appliedSelectedOptions = _.cloneDeep(this.selectedOptions)
        this.setValidating(false)
        this.isFiltersApplied = true
        this.$refs.advancedFiltersDropdown.close();
        this.$emit('apply-advanced', this.appliedSelectedOptions);
      } else {
        this.setValidating(true)
      }
    },
    onAdvancedFiltersDropdownClosed() {
      this.selectedOptions = []
      this.resetAutocomplete()
    },
    onAdvancedFiltersDropdownOpened() {
      if (this.appliedSelectedOptions.length > 0) {
        this.resetAppliedOptions()
      } else {
        this.isPristine = true
        this.addNew()
      }
    },
    /**
     * restore AO after expand component
     * we need it because for hide/show AO component should be restored last applied AO data
     */
    resetAppliedOptions() {
      this.selectedOptions = _.cloneDeep(this.appliedSelectedOptions)
    },
    setValidating(validating) {
      this.isValidating = validating
    },
    share() {
      this.$emit('share');
      this.$refs.advancedFiltersMenu.toggle();
    },
    download() {
      this.$emit('export');
      this.$refs.advancedFiltersMenu.toggle();
    },
    /**
     * Programmatically set applied options (for external control)
     * @param {Array} options - Array of applied filter options
     */
    setAppliedOptions(options) {
      this.appliedSelectedOptions = _.cloneDeep(options);
      this.isFiltersApplied = options.length > 0;
      this.isPristine = options.length === 0;
    }

  },
}
</script>

<style scoped
  lang="scss">
  .advanced-filters {
    &__top-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__buttons-holder {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__dropdown {
      width: 500px;
    }

    &__rows-holder {
      padding: 0 8px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__rows {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__title {
      font-size: 10px;
      line-height: 12px;
      color: $rfx-light-march;
      margin: 0;
    }

    &__chips-container {
      padding-left: 30px;
      margin-top: 8px;
    }

    &__menu-button {
      border: none;
      background: transparent;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      padding: 0;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: $rfx-medium-steel;
      }
    }

    &__menu-dropdown {
      margin: 0;
      width: 160px;
      list-style-type: none;
    }

    &__menu-item {
      padding: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      border-radius: 2px;

      &:hover {
        background-color: $rfx-medium-steel;
      }
    }

    &__menu-icon {
      font-size: 16px;
    }
  }

  .collapsible-block {
    &::v-deep .collapsible__content {
      overflow: visible;
    }
  }

  .advanced-no-data {
    font-size: 22px;
    text-transform: uppercase;
    color: $rfx-pale-grey;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 30px;
  }

  .advanced-controls {
    display: flex;
    flex-direction: column;
    padding-top: 8px;

    &__main-line,
    &__extra-line {
      display: flex;
      align-items: center;
    }

    &__extra-line {
      padding-left: 8px;
    }

    &__main-line {
      justify-content: flex-end;
    }
  }

  .advanced-button {
    height: 40px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    margin-left: 30px;
    border: none;
    outline: none;
    cursor: pointer;

    &_apply {
      background-color: $rfx-medium-march;
      color: #fff;
      width: 100px;
    }

    &_disabled {
      color: $rfx-neutral-grey !important;
      pointer-events: none;
      background-color: transparent;
      cursor: default;
    }

    &_clear {
      background-color: transparent;
      color: $rfx-dark-march;
      width: 100px;
    }

    &_add-new {
      background: transparent;
      display: flex;
      align-items: center;
      gap: 4px;
      text-transform: none;
      font-size: 12px;
      font-weight: 400;
      color: $rfx-quarter-march;
      margin: 0;
      padding: 0;

      &:hover {
        color: $rfx-dark-march;
      }

      &_disabled {
        opacity: .5;
        pointer-events: none;
      }
    }

    &__icon {
      color: inherit;
    }
  }
</style>
